import React, { useState, useMemo } from "react";
import AppHeader from "../AppHeader";
import { useProductView } from "./useProductView";
import styles from "../../styles/Product.module.css";
import { currencyFormat, resolveListingImageUrls, resolveListingTitle, isGenericTrimLabel, useCurrentUser, LISTING_IMAGE_FALLBACK } from "../../utils";
import {
  parseListingVideoEntries,
  resolveListingVideoUrl,
} from "../../utils/listingMedia";
import Loading from "../Loading";
import MiniMap from "../MiniMap";
import PublicComments from "../PublicComments";
import ContactSellerButton from "../ContactSellerButton";
import ImageLightbox from "./Lightbox";
import SpecSheet from "./SpecSheet";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useDarkMode } from "../../App";
import { useQuery } from '@apollo/client';
import getSellerReviewsQuery from '../../graphql/query/getSellerReviews.query';
import getSellerDisplayNameQuery from '../../graphql/query/getSellerDisplayName.query';
import { useGuestAuth } from '../../context/GuestAuthContext';
import { useNavigate } from 'react-router-dom';

interface SellerInfo {
  id: number;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
}

type TabKey = 'description' | 'details' | 'location' | 'qa';

type GalleryItem =
  | { type: "image"; url: string }
  | { type: "video"; url: string; contentType: string };

const ProductView = () => {
  const { car } = useProductView();
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { openSignIn } = useGuestAuth();
  const [selectedImage, setSelectedImage] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('description');

  const sellerId = car?.sellerId as number | undefined;

  const { data: sellerNameData } = useQuery(getSellerDisplayNameQuery, {
    variables: { sellerId: sellerId as number },
    skip: !sellerId || !currentUser,
  });

  const seller = (sellerNameData?.userById || (car as any)?.seller) as SellerInfo | undefined;

  const { data: reviewsData } = useQuery(getSellerReviewsQuery, {
    variables: { sellerId },
    skip: !sellerId,
  });

  const reviews = reviewsData?.allSellerReviews?.nodes || [];
  const averageRating = reviews.length > 0
    ? reviews.reduce((sum: number, review: any) => sum + review.rating, 0) / reviews.length
    : 0;

  const mediaItems: GalleryItem[] = useMemo(() => {
    const imageUrls = resolveListingImageUrls(car?.imageLoc, { darkMode });
    const images: GalleryItem[] = imageUrls.map((url) => ({
      type: "image",
      url,
    }));
    const videos: GalleryItem[] = parseListingVideoEntries(car?.videoLoc).map(
      (entry) => ({
        type: "video" as const,
        url: resolveListingVideoUrl(entry.key),
        contentType: entry.contentType || "video/mp4",
      })
    );
    return [...images, ...videos];
  }, [car?.imageLoc, car?.videoLoc, darkMode]);

  const images = useMemo(
    () =>
      mediaItems
        .filter((m): m is { type: "image"; url: string } => m.type === "image")
        .map((m) => m.url),
    [mediaItems]
  );

  const selectedMedia = mediaItems[selectedImage] || mediaItems[0];
  const mediaCount = mediaItems.length;

  const handleNextImage = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setSelectedImage((prev) => (prev + 1) % mediaCount);
  };

  const handlePrevImage = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setSelectedImage((prev) => (prev - 1 + mediaCount) % mediaCount);
  };

  if (!car) {
    return (
      <div className="home-page-container">
        <AppHeader />
        <Loading />
      </div>
    );
  }

  const getTransmissionDisplay = () => {
    if (car.transmission) return car.transmission;
    const trim = car.trim?.toLowerCase() || '';
    if (trim.includes('man') || trim.includes('mt') || trim.includes('6-spd m') || trim.includes('5-spd m')) {
      return 'Manual';
    }
    if (trim.includes('auto') || trim.includes('cvt') || trim.includes('at') || trim.includes('6-spd a') || trim.includes('8-spd')) {
      return 'Automatic';
    }
    return null;
  };

  const transmissionDisplay = getTransmissionDisplay();

  // Curated quick-glance chips: the most-asked specs at a glance
  const chips = [
    car.year && { icon: <CalendarTodayIcon />, label: `${car.year}` },
    car.mileage != null && { icon: <SpeedIcon />, label: `${car.mileage.toLocaleString()} mi` },
    transmissionDisplay && { icon: <SettingsIcon />, label: transmissionDisplay },
    car.driveType && { icon: <DirectionsCarIcon />, label: car.driveType },
    car.fuelType && { icon: <LocalGasStationIcon />, label: car.fuelType },
  ].filter(Boolean) as Array<{ icon: React.ReactNode; label: string }>;

  const hasLocation = car.latitude != null && car.longitude != null;

  const openLightboxAt = (index: number) => {
    setSelectedImage(index);
    setLightboxOpen(true);
  };

  const priceDisplay = currencyFormat(parseFloat(car.price?.toString() ?? "0"));
  const displayTitle = resolveListingTitle(car);
  const trimSubtitle =
    car.trim &&
    !isGenericTrimLabel(car.trim) &&
    !displayTitle.includes(car.trim)
      ? car.trim
      : null;
  const isOwnListing = !!car.sellerId && currentUser?.id === car.sellerId;
  const isSold = car.sold === true;
  const isInactive = car.active === false;
  const listingAvailable = !isSold && !isInactive;
  const canContactSeller = !!car.sellerId && !isOwnListing;
  const listingStatus = isSold
    ? { label: 'Sold — no longer available', tone: styles.statusSold }
    : isInactive
      ? { label: 'Listing inactive', tone: styles.statusInactive }
      : { label: 'Available from a private seller', tone: styles.statusAvailable };

  return (
    <div className={`${styles.page} ${darkMode ? styles.dark : ''}`}>
      <AppHeader />

      <div className={styles.utilityBar}>
        <button type="button" className={styles.backButton} onClick={() => navigate('/search')}>
          <ArrowBackIcon />
          Back to listings
        </button>
        <span className={styles.listingId}>Listing #{car.id}</span>
      </div>

      <section className={styles.hero}>
        <div className={styles.gallery}>
          <div className={styles.mainImageFrame}>
            <div
              className={styles.mainImageWrap}
              onClick={() => {
                if (selectedMedia?.type === "image") {
                  openLightboxAt(
                    images.findIndex((url) => url === selectedMedia.url)
                  );
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={
                selectedMedia?.type === "video"
                  ? `Video ${selectedImage + 1} of ${mediaCount}`
                  : `Enlarge photo ${selectedImage + 1} of ${mediaCount}`
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  if (selectedMedia?.type === "image") {
                    openLightboxAt(
                      images.findIndex((url) => url === selectedMedia.url)
                    );
                  }
                }
              }}
            >
              {selectedMedia?.type === "video" ? (
                <video
                  key={selectedMedia.url}
                  className={styles.mainImage}
                  src={selectedMedia.url}
                  controls
                  playsInline
                  preload="metadata"
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
                />
              ) : (
                <>
                  {/* Blurred cover fill so portrait/landscape photos don't leave empty letterbox bars. */}
                  <img
                    src={selectedMedia?.url || LISTING_IMAGE_FALLBACK}
                    className={styles.mainImageBlur}
                    alt=""
                    aria-hidden="true"
                    onError={(e) => { (e.target as HTMLImageElement).src = LISTING_IMAGE_FALLBACK; }}
                  />
                  <img
                    src={selectedMedia?.url || LISTING_IMAGE_FALLBACK}
                    className={styles.mainImage}
                    alt={`${displayTitle} – view ${selectedImage + 1}`}
                    onError={(e) => { (e.target as HTMLImageElement).src = LISTING_IMAGE_FALLBACK; }}
                  />
                  <span className={styles.zoomHint}>
                    <ZoomInIcon /> Click to enlarge
                  </span>
                </>
              )}
              {mediaCount > 1 && (
                <div className={styles.imageCounter}>
                  {selectedImage + 1} / {mediaCount}
                </div>
              )}
            </div>
            {mediaCount > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.navBtn} ${styles.navPrev}`}
                  onClick={handlePrevImage}
                  aria-label="Previous media"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={`${styles.navBtn} ${styles.navNext}`}
                  onClick={handleNextImage}
                  aria-label="Next media"
                >
                  ›
                </button>
              </>
            )}
          </div>

          {mediaCount > 1 && (
            <div className={styles.thumbnailStrip}>
              {mediaItems.map((item, idx: number) => (
                <button
                  key={`${item.type}-${idx}`}
                  type="button"
                  onClick={() => setSelectedImage(idx)}
                  className={`${styles.thumbnailButton} ${selectedImage === idx ? styles.thumbnailActive : ''}`}
                  aria-label={
                    item.type === "video"
                      ? `View video ${idx + 1}`
                      : `View photo ${idx + 1}`
                  }
                  aria-pressed={selectedImage === idx}
                >
                  {item.type === "video" ? (
                    <span
                      className={styles.thumbnail}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#111",
                        color: "#fff",
                      }}
                    >
                      <PlayCircleOutlineIcon />
                    </span>
                  ) : (
                    <img
                      src={item.url}
                      alt=""
                      className={styles.thumbnail}
                      onError={(e) => { (e.target as HTMLImageElement).src = LISTING_IMAGE_FALLBACK; }}
                    />
                  )}
                </button>
              ))}
              {images.length > 0 && (
                <button
                  type="button"
                  className={styles.viewAllPhotos}
                  onClick={() => openLightboxAt(0)}
                >
                  <PhotoLibraryOutlinedIcon />
                  View all {images.length} photos
                </button>
              )}
            </div>
          )}
        </div>

        <aside className={styles.rail}>
          <div className={styles.railCard}>
            <div className={`${styles.railEyebrow} ${listingStatus.tone}`}>
              <span className={styles.availableDot} aria-hidden="true" />
              {listingStatus.label}
            </div>
            <h1 className={styles.carTitle}>
              {displayTitle}
            </h1>
            {trimSubtitle && <p className={styles.carSubtitle}>{trimSubtitle}</p>}

            <div className={styles.priceBlock}>
              <span className={styles.priceLabel}>{isSold ? 'Sold for' : 'Asking price'}</span>
              <div className={styles.price}>{priceDisplay}</div>
            </div>

            {chips.length > 0 && (
              <div className={styles.chipRow}>
                {chips.map((chip, idx) => (
                  <span key={idx} className={styles.chip}>
                    {chip.icon}
                    {chip.label}
                  </span>
                ))}
              </div>
            )}

            {car.locationAddress && (
              <p className={styles.railLocation}>
                <LocationOnIcon />
                {car.locationAddress}
              </p>
            )}

            {(car.vin || car.hasVerifiedTitle) && (
              <div className={styles.badges}>
                {car.vin && (
                  <span className={styles.badgeGreen}>
                    <CheckCircleIcon /> VIN Verified
                  </span>
                )}
                {car.hasVerifiedTitle && (
                  <span className={styles.badgeOrange}>
                    <VerifiedIcon /> Title on File
                  </span>
                )}
              </div>
            )}

            {canContactSeller && (
              <div className={styles.contactWrapper}>
                <ContactSellerButton
                  listingId={car.id}
                  sellerId={car.sellerId!}
                  currentUserId={currentUser?.id || 0}
                  darkMode={darkMode}
                  listingAvailable={listingAvailable}
                  listingInfo={{
                    make: car.make || '',
                    model: car.model || '',
                    year: car.year || 0,
                    trim: car.trim || undefined,
                    price: car.price?.toString() || "0",
                    imageLoc: car.imageLoc,
                  }}
                />
              </div>
            )}

            {canContactSeller && listingAvailable && (
              <div className={styles.messagePromise}>
                <ChatBubbleOutlineIcon />
                <span>
                  {currentUser?.id ? (
                    <><strong>Message the seller directly.</strong> No dealer calls or platform fees.</>
                  ) : (
                    <><strong>Log in to message the seller.</strong> Browsing is free — messaging keeps the marketplace spam-free.</>
                  )}
                </span>
              </div>
            )}

            {isOwnListing && (
              <p className={styles.ownListingNote}>This is your listing. Buyers can message you from here.</p>
            )}

            {car.vin && (
              <div
                className={styles.carfaxContainer}
                onClick={() => setShowSnackbar(true)}
              >
                <div className={styles.carfaxLogo}>
                  <span className={styles.carfaxC}>C</span>
                  <span className={styles.carfaxA}>A</span>
                  <span className={styles.carfaxR}>R</span>
                  <span className={styles.carfaxF}>F</span>
                  <span className={styles.carfaxA2}>A</span>
                  <span className={styles.carfaxX}>X</span>
                </div>
                <span className={styles.carfaxLink}>View Carfax history report</span>
              </div>
            )}
          </div>

          {sellerId && (
            <div className={styles.sellerCard}>
              <div className={styles.sellerCardHeader}>
                <h2 className={styles.sellerCardTitle}>Meet the seller</h2>
                <span className={styles.verifiedSeller}><VerifiedIcon /> DriveNow seller</span>
              </div>
              <div className={styles.sellerContent}>
                <div className={styles.sellerAvatar}>
                  {currentUser && seller?.firstname
                    ? seller.firstname.charAt(0).toUpperCase()
                    : '?'}
                </div>
                <div className={styles.sellerInfo}>
                  {currentUser ? (
                    <h3 className={styles.sellerName}>
                      {seller?.firstname
                        ? `${seller.firstname} ${seller.lastname || ''}`.trim()
                        : 'Seller'}
                    </h3>
                  ) : (
                    <button
                      type="button"
                      className={styles.sellerNameLogin}
                      onClick={openSignIn}
                    >
                      Log in to see seller name
                    </button>
                  )}
                  {reviews.length > 0 ? (
                    <div className={styles.sellerRating}>
                      <div className={styles.stars}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <StarIcon
                            key={star}
                            className={star <= Math.round(averageRating) ? styles.starFilled : styles.starEmpty}
                          />
                        ))}
                      </div>
                      <span className={styles.ratingText}>
                        {averageRating.toFixed(1)} ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                  ) : (
                    <p className={styles.noReviews}>No reviews yet</p>
                  )}
                </div>
              </div>
              <div className={styles.safetyNote}>
                <ShieldOutlinedIcon />
                <span>For your safety, keep conversations in DriveNow.</span>
              </div>
            </div>
          )}
        </aside>
      </section>

      <section className={styles.tabsSection}>
        <div className={styles.sectionIntro}>
          <span className={styles.sectionEyebrow}>Everything you need to know</span>
          <h2>Explore this {car.make} {car.model}</h2>
        </div>
        <div className={styles.tabsBar}>
          <div className={styles.tabsHeader}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              textColor={darkMode ? 'inherit' : 'primary'}
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  color: darkMode ? '#bbb' : undefined,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.92rem',
                },
                '& .Mui-selected': {
                  color: darkMode ? '#64b5f6 !important' : undefined,
                },
              }}
            >
              <Tab value="description" label="Description" />
              <Tab value="details" label="Details" />
              {hasLocation && <Tab value="location" label="Location" />}
              {car.sellerId && <Tab value="qa" label="Q&A" />}
            </Tabs>
          </div>

          <div className={styles.tabPanel}>
            {activeTab === 'description' && (
              <div className={styles.descriptionLayout}>
                <div>
                  <h3 className={styles.panelTitle}>Seller's description</h3>
                  <p className={`${styles.description} ${!car.description ? styles.descriptionEmpty : ''}`}>
                    {car.description || "No description provided by the seller."}
                  </p>
                </div>
                <div className={styles.quickFacts}>
                  <span className={styles.quickFactsLabel}>At a glance</span>
                  {chips.slice(0, 4).map((chip, idx) => (
                    <div key={idx} className={styles.quickFact}>
                      {chip.icon}
                      <span>{chip.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <SpecSheet car={car} transmissionDisplay={transmissionDisplay} />
            )}

            {activeTab === 'location' && hasLocation && (
              <>
                <div className={styles.miniMapWrapper}>
                  <MiniMap
                    latitude={parseFloat(car.latitude!.toString())}
                    longitude={parseFloat(car.longitude!.toString())}
                    address={car.locationAddress || undefined}
                    darkMode={darkMode}
                  />
                </div>
                <p className={styles.locationText}>
                  <LocationOnIcon /> {car.locationAddress || "Location approximate"}
                </p>
              </>
            )}

            {activeTab === 'qa' && car.sellerId && (
              <PublicComments
                listingId={car.id}
                sellerId={car.sellerId}
                darkMode={darkMode}
              />
            )}
          </div>
        </div>
      </section>

      {/* Spacer + fixed mobile action bar: spacer reserves scroll room so the bar
          never covers the last description/details/Q&A content. */}
      <div className={styles.mobileBottomBarSpacer} aria-hidden="true" />
      <div className={styles.mobileBottomBar}>
        <div className={styles.mobileBottomPrice}>
          <span className={styles.mobileBottomPriceValue}>{priceDisplay}</span>
          <span className={styles.mobileBottomPriceLabel}>{isSold ? 'Sold for' : 'Asking price'}</span>
        </div>
        {canContactSeller && (
          <div className={styles.mobileBottomAction}>
            <ContactSellerButton
              listingId={car.id}
              sellerId={car.sellerId!}
              currentUserId={currentUser?.id || 0}
              darkMode={darkMode}
              listingAvailable={listingAvailable}
              listingInfo={{
                make: car.make || '',
                model: car.model || '',
                year: car.year || 0,
                trim: car.trim || undefined,
                price: car.price?.toString() || "0",
                imageLoc: car.imageLoc,
              }}
            />
          </div>
        )}
      </div>

      <ImageLightbox
        images={images}
        open={lightboxOpen}
        index={selectedImage}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setSelectedImage}
        darkMode={darkMode}
      />

      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Vehicle history reports coming soon! This feature is not yet implemented.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProductView;
